import { createSelector, createStructuredSelector } from 'reselect';
import movieSelector from 'selectors/movie';
import { translate as translateSelector } from 'selectors/translate';
import subscriptionSelector from 'selectors/subscription';
import _ from 'lodash';

const typePropSelector = (state, { type }) => type;

export const templateTitleSelector = createSelector(
  typePropSelector,
  movieSelector,
  subscriptionSelector,
  translateSelector,
  (type, { movieInfo: movie }, { subscriptionInfo }, translate) => {
    let titledObject;
    let phrase;
    switch (type) {
      case 'subscription':
        phrase = translate('subscription_purchase');
        titledObject = subscriptionInfo;
        break;
      case 'buy':
        phrase = translate('buy_movie');
        titledObject = movie;
        break;
      case 'rent':
        phrase = translate('rent_movie');
        titledObject = movie;
        break;
      default:
        phrase = '';
    }
    return `${phrase}\n«${_.get(titledObject, 'title', '')}»`;
  }
);

export const templateSubtitleSelector = createSelector(
  typePropSelector,
  movieSelector,
  subscriptionSelector,
  translateSelector,
  (type, { paidTariff }, { subscriptionTariff }, translate) => {
    let result;
    const forText = translate('for');
    const asterisk = _.get(subscriptionTariff, 'isTryAndBuy') ? '*' : '';
    switch (type) {
      case 'subscription':
        result = `${_.get(subscriptionTariff, 'title', '')} ${forText} ${_.get(
          subscriptionTariff,
          'priceText',
          ''
        )}${asterisk}`;
        break;
      case 'buy':
      case 'rent':
        result = `${_.get(paidTariff, 'qualityGroup', '')} ${forText} ${_.get(
          paidTariff,
          'priceText',
          ''
        )}`;
        break;
      default:
        result = '';
    }
    return result;
  }
);

export const tariffByIdSelector = createSelector(
  movieSelector,
  subscriptionSelector,
  ({ paidTariff }, { subscriptionTariff }) => paidTariff || subscriptionTariff || null
);

export const buyingTariffsSelector = createSelector(
  typePropSelector,
  movieSelector,
  subscriptionSelector,
  (type, { buyingTariffsInfo }, { subscriptionInfo }) => {
    let result;
    if (type === 'buy') {
      result = _.get(buyingTariffsInfo, 'dto', []);
    } else if (type === 'rent') {
      result = _.get(buyingTariffsInfo, 'tvod', []);
    } else if (type === 'subscription') {
      result = _.get(subscriptionInfo, 'tariffs', []);
    } else {
      result = [];
    }
    return result;
  }
);

export const tariffPaymentSystemsSelector = createSelector(tariffByIdSelector, (tariff) =>
  _.get(tariff, 'paymentSystems', [])
);

export const allPaymentSystems = (state) => _.get(state, 'configuration.paymentSystems', []);

export const filteredPaymentSystemsSelector = createSelector(
  tariffPaymentSystemsSelector,
  allPaymentSystems,
  (tariffSystemIds, systems) => {
    const result = [];
    for (let i = 0, l = systems.length; i < l; ++i) {
      if (_.includes(tariffSystemIds, systems[i].id)) {
        result.push(systems[i]);
      }
    }
    return result;
  }
);

export const paymentSystemsMapSelector = createSelector(
  filteredPaymentSystemsSelector,
  (systems) => {
    const result = {};
    let name;
    for (let i = 0, l = systems.length; i < l; ++i) {
      name = _.get(systems[i], 'provider.name');
      if (name) {
        if (!result[name]) {
          result[name] = [];
        }
        result[name].push(systems[i]);
      }
    }
    return result;
  }
);

export const cardsIsPresentSelector = createSelector(
  paymentSystemsMapSelector,
  (systemsMap) => !!_.get(systemsMap, 'cards')
);

export const cardsServiceUrlSelector = createSelector(paymentSystemsMapSelector, (systemsMap) =>
  _.get(systemsMap, 'cards.0.provider.service_url', '')
);

export default createStructuredSelector({
  templateTitle: templateTitleSelector,
  templateSubtitle: templateSubtitleSelector,
  buyingTariffs: buyingTariffsSelector,
  cardsIsPresent: cardsIsPresentSelector,
  cardsServiceUrl: cardsServiceUrlSelector,
  tariffPaymentSystems: tariffPaymentSystemsSelector,
  filteredPaymentSystems: filteredPaymentSystemsSelector,
  paymentSystemsMap: paymentSystemsMapSelector,
  tariffById: tariffByIdSelector,
});
